<script lang="ts">
export default {
  name: 'Zoom',
};
</script>

<script setup lang="ts">
import { StoreMutationType } from '@/types/StoreMutationType';
import CanvasUtils from '@/utilities/tower/v2/Canvas';
import { percentage } from '@/utilities/Number';
import { computed, inject } from 'vue';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Minus, Plus } from 'lucide-vue-next';

const props = defineProps<{
  bgColor?: string;
  borderColor?: string;
  hoverColor?: string;
}>();

const store = inject<Store<AppState>>('store');

// ----------
// computed
// ----------
const zoomPercent = computed<number>(() => {
  const zoom = zoomLevel.value;
  const minZoom = CanvasUtils.MIN_ZOOM;
  const defaultZoom = CanvasUtils.DEFAULT_ZOOM;
  const maxZoom = CanvasUtils.MAX_ZOOM;
  let percent;

  // Normalize Zoom, min to default is 10-100, default to max is 100-500
  if (zoom <= defaultZoom && zoom >= minZoom) {
    percent = 10 + ((zoom - minZoom) / (defaultZoom - minZoom)) * 90;
  } else {
    percent = 100 + ((zoom - defaultZoom) / (maxZoom - defaultZoom)) * 400;
  }

  return percent;
});
const buttonBgColor = computed<string>(() => {
  return props.bgColor ?? 'tower-alt';
});

const buttonBorderColor = computed<string>(() => {
  return props.borderColor ?? 'tower-border';
});

const buttonHoverColor = computed<string>(() => {
  return props.hoverColor ?? 'tower-selected';
});

const formattedZoomPercent = computed<string | undefined>(() => {
  return percentage(zoomPercent.value, 0, 0);
});

const zoomLevel = computed<number>(() => {
  return store?.state.towerCanvasState.zoomLevel ?? CanvasUtils.DEFAULT_ZOOM;
});

// ----------
// methods
// ----------
function zoomOut() {
  let zoom = store?.state.towerCanvasState.zoomLevel;
  if (!zoom || !zoomPercent.value) return;

  zoom = getZoomLevelFromPercent(zoomPercent.value - CanvasUtils.ZOOM_PERCENT_STEP);
  
  if (zoom === undefined) { return }

  if (zoom < CanvasUtils.MIN_ZOOM) {
    zoom = CanvasUtils.MIN_ZOOM;
  }

  store?.commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, zoom);
}

function zoomIn() {
  let zoom = store?.state.towerCanvasState.zoomLevel;
  if (!zoom || !zoomPercent.value) return;

  zoom = getZoomLevelFromPercent(zoomPercent.value + CanvasUtils.ZOOM_PERCENT_STEP);
  if (zoom === undefined) { return }

  if (zoom > CanvasUtils.MAX_ZOOM) {
    zoom = CanvasUtils.MAX_ZOOM;
  }

  store?.commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, zoom);
}

function getZoomLevelFromPercent(percent: number): number | undefined {
  const minZoom = CanvasUtils.MIN_ZOOM;
  const defaultZoom = CanvasUtils.DEFAULT_ZOOM;
  const maxZoom = CanvasUtils.MAX_ZOOM;
  let zoom;

  if (percent <= 100 && percent >= 10) {
    zoom = minZoom + ((percent - 10) / 90) * (defaultZoom - minZoom);
  } else {
    zoom = defaultZoom + ((percent - 100) / 400) * (maxZoom - defaultZoom);
  }

  return zoom;
}

function resetZoom() {
  store?.commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, CanvasUtils.DEFAULT_ZOOM);
}
</script>

<template>
  <section class="tw:pointer-events-auto tw:self-end tw:p-2 tw:pr-1">
    <div class="tw:box-border tw:flex">
      <button
        class="tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-l-lg tw:border tw:border-solid tw:text-2xl tw:shadow-lg tw:ease-in"
        :class="`tw:bg-${buttonBgColor} tw:border-${buttonBorderColor} tw:hover:bg-${buttonHoverColor}`"
        @click="zoomOut"
      >
        <Minus :size="18" />
      </button>

      <button
        class="tw:text tw:mx-1 tw:flex tw:items-center tw:justify-center tw:border tw:border-solid tw:px-2 tw:shadow-lg"
        :class="`tw:bg-${buttonBgColor} tw:border-${buttonBorderColor} tw:hover:bg-${buttonHoverColor}`"
        @click="resetZoom"
      >
        <span class="tw:w-full tw:grow">{{ formattedZoomPercent }}</span>
      </button>

      <button
        class="tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-r-lg tw:border tw:border-solid tw:text-2xl tw:shadow-lg tw:ease-in"
        :class="`tw:bg-${buttonBgColor} tw:border-${buttonBorderColor} tw:hover:bg-${buttonHoverColor}`"
        @click="zoomIn"
      >
        <Plus :size="18" />
      </button>
    </div>
  </section>
</template>
